import moment from "moment";
import {
  TableCellType,
  TableHeader,
} from "../../../../components/custom-table/table";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import {
  JCodeDetails,
  MedicalPaOrder,
} from "../../../../data-model/medicalPaOrdersModel";
import { EventsName } from "../../../../enums/eventsName";
import { MedicalPaOrdersAuthStatus } from "../../../../enums/medicalPaOrdersAuthStatus";
import { CptCodeWithDetailsKeys } from "../../../../enums/tableColumnKeys";
import { getAuthStatusOfOneJCode } from "../../../../utils/getAuthStatusOfMedicalPaOrder";
import {
  checkIfDateIsEarlierThanDateOfService,
  checkIfDateIsInCorrectFormat,
  checkIfDateIsLaterThanDateOfService,
  checkIfJCodeFieldHasError,
  checkIfNumVisitsIsBlank,
  checkIfNumVisitsMatchesRemainingAndCompleted,
  checkIfVisitCompletedIsBlank,
  checkIfVisitsRemainingIsBlank,
} from "./utils/computeErrorStates";
import { determineIfTheJCodeFieldShouldBeEditable } from "./utils/determineIfTheFieldShouldBeEditable";

export const CptCodesTableColumnsKeys: TableHeader[] = [
  {
    label: "Date From",
    key: CptCodeWithDetailsKeys.StartFrom,
    order: 1,
    width: 10,
    sortable: true,
    type: TableCellType.EDITABLE_TEXT,
  },
  {
    label: "Date To",
    key: CptCodeWithDetailsKeys.EndAt,
    order: 1,
    width: 10,
    sortable: true,
    type: TableCellType.EDITABLE_TEXT,
  },
  {
    label: "J Codes",
    key: CptCodeWithDetailsKeys.JCodes,
    order: 3,
    width: 10,
    type: TableCellType.TEXT_CELL_WITH_INPUT,
  },
  {
    label: "Description",
    key: CptCodeWithDetailsKeys.Description,
    order: 4,
    width: 25,
    type: TableCellType.STRING_WITH_ICON,
  },
  {
    label: "Total Visits",
    key: CptCodeWithDetailsKeys.Visits,
    order: 5,
    width: 10,

    type: TableCellType.EDITABLE_TEXT,
  },
  {
    label: "Remaining",
    key: CptCodeWithDetailsKeys.RemainingVisits,
    order: 6,
    width: 10,

    type: TableCellType.EDITABLE_TEXT,
  },
  {
    label: "Completed",
    key: CptCodeWithDetailsKeys.VisitsAllowed,
    order: 7,
    width: 10,

    type: TableCellType.EDITABLE_TEXT,
  },
  {
    label: "Auth Required",
    key: CptCodeWithDetailsKeys.PaRequired,
    order: 8,
    width: 15,
    type: TableCellType.SELECTABLE_BADGE,
  },
];

export const generateCptCodesTableData = (
  data: MedicalPaOrder,
  authStatusOptions: AuthStatusOptionModel[],
) => {
  const authEntries = data?.auth_on_file?.auth_entries ?? [];
  const getAllJCodesData = () => {
    let JcodesData: JCodeDetails[] = [];
    authEntries.forEach((item) => {
      JcodesData.push(...(item?.j_codes ?? []));
    });
    return JcodesData;
  };
  const JcodesData = getAllJCodesData();
  return JcodesData.map((item, index) => {
    const authStatus = getAuthStatusOfOneJCode(item, authStatusOptions);

    const description = () => {
      if (item?.description && item?.description !== "") {
        return item?.description;
      }
      return item?.medicine_name?.join(", ") ?? "N/A";
    };

    return {
      [CptCodeWithDetailsKeys.StartFrom]: {
        data: item?.start_date
          ? moment(item.start_date).format("MM/DD/YYYY")
          : "",
        hasError:
          authStatus?.id === MedicalPaOrdersAuthStatus.AuthOnFile ||
          authStatus?.id === MedicalPaOrdersAuthStatus.AuthByRISA
            ? !checkIfDateIsInCorrectFormat(
                item?.start_date ?? "",
                "MM/DD/YYYY",
              ) ||
              !checkIfDateIsEarlierThanDateOfService(
                item?.start_date ?? "",
                data?.payload?.date_of_service ?? "",
              )
            : false,
      },
      [CptCodeWithDetailsKeys.EndAt]: {
        data: item?.end_date ? moment(item.end_date).format("MM/DD/YYYY") : "",
        hasError:
          authStatus?.id === MedicalPaOrdersAuthStatus.AuthOnFile ||
          authStatus?.id === MedicalPaOrdersAuthStatus.AuthByRISA
            ? !checkIfDateIsInCorrectFormat(
                item?.end_date ?? "",
                "MM/DD/YYYY",
              ) ||
              !checkIfDateIsLaterThanDateOfService(
                item?.end_date ?? "",
                data?.payload?.date_of_service ?? "",
              )
            : false,
      },
      [CptCodeWithDetailsKeys.Description]: {
        text: description(),
        shouldShowBlockedIcon:
          item?.nccn_match === EventsName.NCCN_CONFIG_MISSING,
        shouldShowAlertIcon:
          item?.drug_preference === EventsName.DRUG_PREFERENCE,
        textWidth: "80%",
      },
      [CptCodeWithDetailsKeys.PaRequired]: {
        text: authStatus?.text,
        textColor: authStatus?.textColor,
        bgColor: authStatus?.bgColor,
        currentStatusId: authStatus?.id,
        drug_name: data?.payload?.regimen_name ?? "",
      },
      [CptCodeWithDetailsKeys.Visits]: {
        data: item?.num_visits ?? "",
        hasError:
          authStatus?.id === MedicalPaOrdersAuthStatus.AuthOnFile ||
          authStatus?.id === MedicalPaOrdersAuthStatus.AuthByRISA
            ? checkIfNumVisitsIsBlank(item?.num_visits) ||
              !checkIfNumVisitsMatchesRemainingAndCompleted(
                item?.num_visits,
                item?.visits_remaining,
                item?.visits_happened,
              )
            : false,
      },
      [CptCodeWithDetailsKeys.RemainingVisits]: {
        data: item?.visits_remaining ?? "",
        hasError:
          authStatus?.id === MedicalPaOrdersAuthStatus.AuthOnFile ||
          authStatus?.id === MedicalPaOrdersAuthStatus.AuthByRISA
            ? checkIfVisitsRemainingIsBlank(item?.visits_remaining)
            : false,
      },
      [CptCodeWithDetailsKeys.VisitsAllowed]: {
        data: item?.visits_happened ?? "",
        hasError:
          authStatus?.id === MedicalPaOrdersAuthStatus.AuthOnFile ||
          authStatus?.id === MedicalPaOrdersAuthStatus.AuthByRISA
            ? checkIfVisitCompletedIsBlank(item?.visits_happened)
            : false,
      },
      [CptCodeWithDetailsKeys.JCodes]: {
        data: item?.j_code ?? "",
        id: item?.id ?? "",
        allowInputModification: determineIfTheJCodeFieldShouldBeEditable(
          authStatus?.id,
          item?.j_code ?? "",
        ),
        hasError: checkIfJCodeFieldHasError(authStatus?.id, item?.j_code ?? ""),
      },
      [CptCodeWithDetailsKeys.JCodeID]: item?.id ?? "N/A",
    };
  });
};
